/**
 * The register's downloads: one tag as Excel or PDF, its IOGP 551 detail form, and the package's
 * IOGP 551 overview. Every export carries the citation next to each value; the server builds them.
 *
 * Downloads go through `fetch` rather than a bare link, so a refusal (no register entry yet, a
 * package outside the reader's scope) is shown on the screen in the server's words instead of
 * replacing the page with a JSON error. Viewers may download: the export routes are read-only.
 */
import { BASE, fetchOk, path } from "../../lib/http";

export interface ExportLink {
  label: string;
  href: string;
  /** The name to save under when the server sends none. Matches the server's own naming. */
  filename: string;
}

/** The server's file-name rule: letters, digits, dash and underscore, everything else a dash. */
function safe(name: string): string {
  return name.replace(/[^A-Za-z0-9_-]/g, "-");
}

/**
 * The register entry's screen. Under `/register`, not `/assets`: nginx serves the built bundle
 * from `/assets/`, so an entry's deep link there was answered by the file server, not the app.
 */
export function assetPath(pkg: string, tag: string): string {
  return path`/register/${pkg}/${tag}`;
}

/** A file name the built bundle uses: one segment under /assets with a bundle extension. */
const BUNDLE_FILE = /^\/assets\/[^/]+\.(js|css|map|svg|png|jpe?g|webp|woff2?|ico)$/i;

/**
 * Where an old `/assets/{package}/{tag}` link now lives, or null for anything else, including a
 * bundle file, which is never a screen. The path stays encoded as it arrived, so a tag with a
 * space or a slash survives the redirect.
 */
export function registerPathFromLegacy(pathname: string): string | null {
  if (BUNDLE_FILE.test(pathname)) return null;
  const m = /^\/assets\/([^/]+)\/([^/]+)\/?$/.exec(pathname);
  return m ? `/register/${m[1]}/${m[2]}` : null;
}

export function assetExports(pkg: string, tag: string): ExportLink[] {
  // The API route stays under /assets; only the screen moved to /register.
  const base = `${BASE}${path`/assets/${pkg}/${tag}`}`;
  const stem = safe(`${pkg}-${tag}`);
  return [
    { label: "Excel", href: `${base}/export.xlsx`, filename: `${stem}.xlsx` },
    { label: "PDF", href: `${base}/export.pdf`, filename: `${stem}.pdf` },
    { label: "IOGP 551 detail", href: `${base}/export.iogp551.xlsx`, filename: `${stem}-iogp551.xlsx` },
  ];
}

export function overviewExport(pkg: string): ExportLink {
  return {
    label: "IOGP 551 overview",
    href: `${BASE}${path`/assets/${pkg}`}/export.iogp551-overview.xlsx`,
    filename: `${safe(pkg)}-iogp551-overview.xlsx`,
  };
}

function filenameFrom(header: string | null): string | null {
  const match = /filename="?([^";]+)"?/i.exec(header ?? "");
  return match ? match[1] : null;
}

/** Hand the file to the browser's download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Fetch an export and save it, or throw an ApiError with the server's reason. Through the request
 * core, so an ended session signs in again and comes back here, as every other call does.
 */
export async function download(
  link: ExportLink,
  save: (blob: Blob, filename: string) => void = saveBlob,
): Promise<void> {
  const res = await fetchOk(link.href);
  save(await res.blob(), filenameFrom(res.headers.get("Content-Disposition")) ?? link.filename);
}
